// Taşeron portalı — personel çekmecesinin belge listesi (GL5; §15.2 yol B; KVKK). Kayıt (Personelim) ve
// izin bağlamında AYNI bileşen ve AYNI eylemler: indir, (yalnız `Pending`) sil, yükle. Sunum araç
// çekmecesinin (`VehicleDocuments`): başlık + ikincil "Belge yükle" (R7 — iki kardeş çekmece aynı eylemi
// farklı ağırlıkta göstermez), sr-only başlıklı `DocumentRow` tablosu. Satır menüsü (⋮) YOK — çekmece
// içinde `--z-dropdown` menüsü perdenin altında kalırdı; eylemler ortak `DocumentActions` slotlarıdır.
//
// **Yükleme (S19-D9).** Düğme + bölüme sürükle-bırak → kişi-kapsamlı (`subject`) `UploadModal`.
// `subject.userUuid` başlatma isteğine `subjectUserUuid` olarak geçer; AI'nin okuduğu adın pinlenmiş
// kişiden farklı çıkması yanlış kişiye yüklemeyi yakalayan TEK şeydir, kablolama bu yüzden önemlidir.
// Bu bileşen `startDocumentAnalysis`'ı KENDİSİ ÇAĞIRMAZ (S19-D5 tek üretici): yalnız `UploadModal`
// aracılığıyla `DocumentAnalysisProvider`'ın tek kuyruğuna dosya sürer. Modal kapanınca çekmece AÇIK
// kalır ve odak "Belge yükle"ye döner; analizin seyri `PersonnelAnalysisStatus`'ta görünür (R1).
//
// ⚠️ **İki GUID.** `uuid` = link kaydı → SİLME; `documentUuid` = §8 dosyası → İNDİRME. Karıştırılırsa
// silme `404 PERSONNEL_DOCUMENT_NOT_FOUND` verir.
//
// **S29 — silme yalnız `Pending`de.** Onay/red İSG uzmanının kaydıdır; taşeron onu silemez, yenisini
// yükler (aynı türde birden çok satır olağandır). Liste bayat olabilir (uzman arada karar verir): düğme
// `Pending`e göre gizlenir (nezaket) VE `409 PERSONNEL_DOCUMENT_NOT_PENDING` dönerse hata onay kutusunda
// okunur, liste tazelenir (kapı backend'dedir). Onaylı satırda kilit satırı YOK (R8: çoğu satır onaylıdır,
// "istisna yüksek sesle, olağan sessiz").
//
// **KVKK/PII.** İndirme §8 presigned GET ile kısa ömürlü; UI PII/dosya URL'i log'lamaz.

import {
  Button,
  ConfirmDialog,
  DOCUMENT_ACCEPT_ATTR,
  DocumentRow,
  EmptyState,
  type PersonnelDocumentDto,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  currentCulture,
  documentNameDetail,
  documentValidityText,
  presentDocument,
  resolveApiErrorMessage,
  supplierContractorApi,
  useDocumentTypes,
  validateDocumentFile,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CloudUpload, FileText, Upload } from 'lucide-react';
import { type DragEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentAnalysisQueue } from '../../lib/DocumentAnalysisProvider';
import { formatDate } from '../../lib/format';
import { supplierContractorKeys } from '../../lib/supplierContractorKeys';
import { useReturnFocus } from '../../lib/useReturnFocus';
import { StrandedAnalysesStrip } from '../StrandedAnalysesStrip';
import { UploadModal } from '../UploadModal';
import { DocumentActions } from '../documents/DocumentActions';
import { PersonnelAnalysisStatus } from './PersonnelAnalysisStatus';

export interface PersonnelDocumentsProps {
  companyId: number | null;
  /** `SupplierUser.Uuid` (kişi) — bağ satırı uuid'i DEĞİL. */
  userUuid: string;
  /** `personLabel(kişi)` — `UploadModal`'ın kişi-kapsam satırı ("… için"). Boşsa yükleme gizlenir. */
  personName: string;
}

const NO_DOCUMENTS: PersonnelDocumentDto[] = [];

export function PersonnelDocuments({ companyId, userUuid, personName }: PersonnelDocumentsProps) {
  const { t } = useTranslation();
  // Tür etiketi §27 kaydından (`personnel`; kayıtta yoksa ham kod) — GL #7.
  const { label: typeLabelOf } = useDocumentTypes();
  const culture = currentCulture();
  const queryClient = useQueryClient();

  const [uploadOpen, setUploadOpen] = useState(false);
  // `UploadModal` sözleşmesi: her seçim için YENİ bir `File[]` referansı (modal aynı diziyi tek sefer
  // işler) — input da drop da `Array.from` ile taze dizi üretir.
  const [pickedFiles, setPickedFiles] = useState<File[]>([]);
  // Sağlayıcının son partisini bu çekmece açtı mı (bağlam partinin kişisini taşımaz). Analiz bitince
  // `onSettled` indirir; analiz doğurmayan parti modal kapanınca iner (aşağıda); çekmece kapanınca
  // bileşenle birlikte söner.
  const [uploadedHere, setUploadedHere] = useState(false);
  const { queue, analysisUuid } = useDocumentAnalysisQueue(); // yalnız OKUNUR
  const [toDelete, setToDelete] = useState<PersonnelDocumentDto | null>(null);
  const [deletedUuid, setDeletedUuid] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Modal kapanınca odak "Belge yükle"ye döner — sürükle-bırakta tıklanan bir tetikleyici yoktur.
  const uploadTrigger = useReturnFocus();
  // Sürükleme sayacı — `dragleave` alt öğelerin üzerinden geçerken de ateşlenir; boolean titrerdi.
  const dragDepthRef = useRef(0);
  const [dragOver, setDragOver] = useState(false);

  const query = useQuery({
    queryKey: supplierContractorKeys.personnelDocuments(companyId, userUuid),
    queryFn: () => supplierContractorApi.listSupplierPersonnelDocuments(userUuid),
  });
  // Arka plan yenilemesi hata verse de eldeki liste kalır; hata yalnız hiç veri yokken gösterilir.
  const documents = query.data;
  const rows = documents ?? NO_DOCUMENTS;
  const canUpload = documents !== undefined && personName !== '';

  const deleteMutation = useMutation({
    // SİLME: link kaydının kendi `uuid`'i (§8 `documentUuid` DEĞİL — aksi 404).
    mutationFn: (linkUuid: string) =>
      supplierContractorApi.deleteSupplierPersonnelDocument(userUuid, linkUuid),
    onSuccess: (_data, linkUuid) => {
      queryClient.invalidateQueries({
        queryKey: supplierContractorKeys.personnelDocuments(companyId, userUuid),
      });
      setDeletedUuid(linkUuid);
      setToDelete(null);
    },
    // S29: `409 PERSONNEL_DOCUMENT_NOT_PENDING` (uzman arada karar verdi) ya da `404` (başka yerde
    // silindi) — kutu açık kalır, hata orada okunur; liste tazelenir ki satır doğru durumu taşısın ve
    // kullanıcı aynı `409`a tekrar tekrar çarpmasın.
    onError: (_error, linkUuid) => {
      queryClient.invalidateQueries({
        queryKey: supplierContractorKeys.personnelDocuments(companyId, userUuid),
      });
      setDeletedUuid(linkUuid);
    },
  });

  // Silinen satır kalkınca (ya da yenilemede artık silinemez olunca) sil düğmesi DOM'dan gider; odak
  // <body>'ye düşmesin, "Belge yükle"ye insin. Modal'ın kapanış dönüşü kopmuş düğmeye dokunmaz.
  useEffect(() => {
    if (
      deletedUuid !== null &&
      toDelete === null &&
      !rows.some((row) => row.uuid === deletedUuid && row.approvalStatus === 'Pending')
    ) {
      uploadTrigger.ref.current?.focus();
      setDeletedUuid(null);
    }
  }, [rows, deletedUuid, toDelete, uploadTrigger.ref]);

  // Bayrak `UploadModal`'ın efekti partiyi başlattıktan (`startUpload`) SONRA kurulur — ebeveyn efekti
  // çocuğunkinden sonra çalışır. Aynı karede kurulsaydı önceki partinin (ör. Belgeler) `waitingForSlot`/
  // `startError`'ı bir kare bu kişinin durum yuvasında görünür ve duyurulabilirdi. Hiçbir dosya
  // modalın kendi süzgecinden (`validateDocumentFile`) geçmiyorsa modal `startUpload`'ı hiç çağırmaz:
  // sağlayıcı durumu bu partinin DEĞİLDİR, bayrak kurulmaz.
  useEffect(() => {
    if (uploadOpen && pickedFiles.some((file) => validateDocumentFile(file) === null)) {
      setUploadedHere(true);
    }
  }, [uploadOpen, pickedFiles]);
  // Analiz doğurmayan parti (vazgeç-sil, yüklemeyi iptal, yalnız okunamayan dosyalarla kaydet): modal
  // kapalı, kuyruk boş, analiz yok → bayrak iner. Anahtar kapalıyken okunabilir dosyalarla elle kayıtta
  // kuyruk `done` satırlarını tutar ve bayrak kalır; o partide başlatma hiç tetiklenmez, yuva boş kalır.
  // Kuyruk doluyken indirmek yanlış olurdu: `Tamam` anında başlatma (`starting`) henüz görünmez.
  useEffect(() => {
    if (uploadedHere && !uploadOpen && queue.length === 0 && analysisUuid === null) {
      setUploadedHere(false);
    }
  }, [uploadedHere, uploadOpen, queue.length, analysisUuid]);
  const handleSettled = useCallback(() => setUploadedHere(false), []);

  function openPicker(files: File[]) {
    if (files.length === 0) {
      return;
    }
    setPickedFiles(files);
    setUploadOpen(true);
  }

  function handleDragEnter(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    if (!canUpload) {
      return;
    }
    dragDepthRef.current += 1;
    setDragOver(true);
  }

  function handleDragOver(e: DragEvent<HTMLElement>) {
    // Tarayıcı `drop`'u yalnız `dragover` engellenirse ateşler (ve dosyayı sekmede açmaz).
    e.preventDefault();
  }

  function handleDragLeave(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setDragOver(false);
    }
  }

  function handleDrop(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragOver(false);
    if (canUpload) {
      openPicker(Array.from(e.dataTransfer.files)); // FileList ANINDA snapshot'lanır
    }
  }

  // `StrandedAnalysesStrip`'in "hâlâ aksıyor mu" türetimi — bu KİŞİNİN listesi (kişi-pinlenmiş analiz
  // §15.2'ye bağlanır, Belgeler'in §13.6 listesine DEĞİL).
  const linkedDocumentUuids = useMemo(
    () => new Set(rows.map((document) => document.documentUuid)),
    [rows],
  );

  const title = t('personnelDocs.title');

  return (
    <div
      className="relative flex flex-col gap-3"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {dragOver ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[var(--z-base)] flex flex-col items-center justify-center gap-1 rounded-sm border-2 border-dashed border-primary bg-bg/80"
        >
          <CloudUpload className="h-8 w-8 text-primary" aria-hidden="true" />
          <p className="text-sm font-medium text-fg">{t('documents.dropHere')}</p>
          <p className="text-xs text-muted">{t('documents.dropHint')}</p>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-fg">{title}</h2>
        {canUpload ? (
          <>
            <Button
              ref={uploadTrigger.ref}
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4" aria-hidden="true" />
              {t('personnelDocs.add')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={DOCUMENT_ACCEPT_ATTR}
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []); // önce snapshot
                e.target.value = ''; // sonra sıfırla (aynı dosya tekrar seçilebilsin)
                openPicker(files);
              }}
            />
          </>
        ) : null}
      </div>

      <div className="flex flex-col">
        <p className="text-xs text-muted">{t('personnelDocs.kvkk')}</p>
        <PersonnelAnalysisStatus
          companyId={companyId}
          userUuid={userUuid}
          uploadedHere={uploadedHere}
          onSettled={handleSettled}
        >
          {canUpload ? (
            <StrandedAnalysesStrip
              companyId={companyId}
              scope={{ kind: 'personnel', userUuid }}
              linkedDocumentUuids={linkedDocumentUuids}
            />
          ) : null}
        </PersonnelAnalysisStatus>
      </div>

      <Table caption={title}>
        <TableHeader>
          <TableRow className="carbon:h-0">
            <TableHead className="h-0 p-0 carbon:h-0">
              <span className="sr-only">{t('personnelDocs.col.document')}</span>
            </TableHead>
            <TableHead className="h-0 p-0 text-right carbon:h-0">
              <span className="sr-only">{t('common.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody aria-busy={query.isPending || undefined}>
          {documents === undefined ? (
            query.isError ? (
              <TableMessageRow colSpan={2}>
                {resolveApiErrorMessage(query.error, t)}
              </TableMessageRow>
            ) : (
              <TableSkeletonRows columns={2} rows={2} />
            )
          ) : rows.length === 0 ? (
            <TableMessageRow colSpan={2}>
              <EmptyState icon={FileText} title={t('personnelDocs.empty')} />
            </TableMessageRow>
          ) : (
            rows.map((document) => {
              const typeLabel = typeLabelOf('personnel', document.type);
              const p = presentDocument(document, t);
              const validity = documentValidityText(document, (iso) => formatDate(iso, culture), t);
              return (
                <DocumentRow
                  key={document.uuid}
                  title={typeLabel}
                  fileName={document.fileName}
                  noteText={document.note}
                  validityRangeText={validity}
                  quiet={p.quiet}
                  quietLabel={p.quietLabel}
                  tags={p.tags}
                  srExtra={p.srExtra}
                  statusContextLabel={t('personnelDocs.col.approval')}
                  rejectionReasonText={
                    document.approvalStatus === 'Rejected' && document.rejectionReason
                      ? t('personnelDocs.rejectionReason', { reason: document.rejectionReason })
                      : null
                  }
                  actions={
                    <DocumentActions
                      doc={document}
                      typeLabel={typeLabel}
                      // Aynı türden iki dosyasız belge de ayrı adlansın: dosya adı, yoksa geçerlilik.
                      nameDetail={documentNameDetail(document, validity)}
                      canDelete={document.approvalStatus === 'Pending'}
                      onDelete={() => setToDelete(document)}
                    />
                  }
                />
              );
            })
          )}
        </TableBody>
      </Table>

      <UploadModal
        open={uploadOpen}
        onOpenChange={(open) => {
          setUploadOpen(open);
          if (!open) {
            uploadTrigger.restore();
          }
        }}
        files={pickedFiles}
        companyId={companyId}
        subject={{ userUuid, name: personName }}
      />
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setToDelete(null);
            deleteMutation.reset();
            // Satır hâlâ silinebilirse (vazgeçildi) bayat odak hedefi bırakma; Modal odağı sil
            // düğmesine döndürür.
            if (
              toDelete &&
              rows.some((row) => row.uuid === toDelete.uuid && row.approvalStatus === 'Pending')
            ) {
              setDeletedUuid(null);
            }
          }
        }}
        title={t('personnelDocs.delete.title')}
        body={t('personnelDocs.delete.body', {
          type: toDelete ? typeLabelOf('personnel', toDelete.type) : '',
        })}
        confirmLabel={t('personnelDocs.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.uuid)}
      />
    </div>
  );
}
